import {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth'
import globalStore from 'store'
import { EventData, LogoutReason, MessageCode, Messages } from 'util/enums/interceptorEnum'

const WINDOW_MS = 10_000
const MAX_400 = 5

interface ApiErrorResponse {
  messageCode?: string
  message?: string
}

// Cross tab-channel communication
// https://developer.mozilla.org/en-US/docs/Web/API/BroadcastChannel
const bc =
  typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel('defender-auth')
    : null

let signoutInFlight = false
let consecutive400 = 0
let timer: number | undefined

function isProtected(url?: string) {
  if (!url) return true

  // ignore auth and public endpoints
  return !/^\/?auth(\/|$)/i.test(url) && !/\/public\//i.test(url)
}

function resetWindow() {
  if (timer) window.clearTimeout(timer)
  timer = window.setTimeout(() => {
    consecutive400 = 0
  }, WINDOW_MS)
}

async function forceSignOut(reason: string) {
  if (signoutInFlight) return
  signoutInFlight = true
  try {
    localStorage.setItem(LogoutReason.KEY_REASON, reason)
    bc?.postMessage({ type: EventData.FORCE_SIGNOUT, reason })
    // Cognito session
    await amplifySignOut()
    // App state
    await globalStore.getState().logout()
    // Clear client caches
    if (window.SWRGlobalMutate) {
      await window.SWRGlobalMutate(() => true, undefined, { revalidate: false })
    }
  } finally {
    window.location.assign('/')
  }
}

// Listen from other tabs
bc?.addEventListener('message', ev => {
  if (ev.data?.type === EventData.FORCE_SIGNOUT) forceSignOut(ev.data.reason)
})

// ---- Requests ----
async function onRequest(
  config: InternalAxiosRequestConfig,
  activeBearer = true,
): Promise<InternalAxiosRequestConfig> {
  const session = await fetchAuthSession().catch(() => null)
  const token = session?.tokens?.accessToken?.toString()

  if (token) {
    if (!config.headers) config.headers = new AxiosHeaders()
    config.headers.set('authorization', activeBearer ? `Bearer ${token}` : token)
  }

  return config
}

function onRequestError(error: AxiosError) {
  return Promise.reject(error)
}

// ---- Responses ----
function onResponse(res: AxiosResponse) {
  consecutive400 = 0
  if (timer) {
    window.clearTimeout(timer)
    timer = undefined
  }
  return res
}

// ---- Requests Error ----
async function onResponseError(error: AxiosError) {
  const status = error.response?.status
  const data = error.response?.data as ApiErrorResponse
  const url = (error.config as AxiosRequestConfig)?.url

  const isUnauthorizedAction =
    status === 401 &&
    data?.messageCode === MessageCode.UNAUTHORIZED &&
    data?.message === Messages.PLEASE_AUTHENTICATE

  const badRequestStatus = status === 400 && data?.messageCode === MessageCode.BAD_REQUEST

  if (!isProtected(url)) return Promise.reject(error)

  if (isUnauthorizedAction) {
    await forceSignOut(LogoutReason.UNAUTHORIZED)
  }

  if (badRequestStatus) {
    consecutive400 += 1
    resetWindow()
    if (consecutive400 >= MAX_400) {
      await forceSignOut(LogoutReason.BAD_REQUEST)
    }
  }

  return Promise.reject(error)
}

export function setupInterceptorsTo(
  axiosInstance: AxiosInstance,
  activeBearer?: boolean,
): AxiosInstance {
  axiosInstance.interceptors.request.use(config => onRequest(config, activeBearer), onRequestError)
  axiosInstance.interceptors.response.use(onResponse, onResponseError)
  return axiosInstance
}
